import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, useTheme, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from '@ui/components/common/DataTable';
import { between } from '@ui/style/style';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getAllPeopleRecentEvents } from '@application/features/people/peopleThunk';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { Calendar2 } from 'iconsax-react';
// Mock data - replace with actual API call
const mockEventsAttended = [
    {
        id: '1',
        eventName: 'F1 Event',
        time: '04:30 PM',
        date: 'Jun 15, 2025',
        interests: ['Football', 'Electronics', 'Robotics', 'Volunteering'],
        eventType: 'Internal'
    },
    {
        id: '2',
        eventName: 'B2 Marathon',
        time: '05:30 PM',
        date: 'Jun 16, 2025',
        interests: ['Running', 'Fitness', 'Health', 'Sports', 'Outdoor', 'Community'],
        eventType: 'Partner'
    },
    {
        id: '3',
        eventName: 'Skate Showdown',
        time: '06:00 PM',
        date: 'Jun 20, 2025',
        interests: [
            'Skating',
            'Sports',
            'Competition',
            'Youth',
            'Entertainment',
            'Recreation',
            'Music',
            'Art'
        ],
        eventType: 'Internal'
    },
    {
        id: '4',
        eventName: 'Trailblazer Hike',
        time: '08:10 PM',
        date: 'Jun 22, 2025',
        interests: ['Hiking', 'Nature'],
        eventType: 'Internal'
    },
    {
        id: '5',
        eventName: 'Soccer Tournament',
        time: '09:35 PM',
        date: 'Jun 23, 2025',
        interests: ['Soccer', 'Team Sports', 'Competition', 'Youth'],
        eventType: 'Partner'
    }
];
const getDateRangeOptions = (t) => [
    { id: 'all', text: t('people.dateRangeOptions.all') },
    { id: '7days', text: t('people.dateRangeOptions.7days') },
    { id: '10days', text: t('people.dateRangeOptions.10days') },
    { id: '30days', text: t('people.dateRangeOptions.30days') },
    { id: '6months', text: t('people.dateRangeOptions.6months') },
    { id: '1year', text: t('people.dateRangeOptions.1year') },
    { id: 'custom', text: t('people.dateRangeOptions.custom') }
];
const EventsAttendedTab = ({ personId }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedRangeOption, setSelectedRangeOption] = useState('all');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const { peopleRecentEvent } = useSelector((State) => State.people);
    const dispatch = useAppDispatch();
    const getDateRanges = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const tenDaysAgo = new Date(today);
        tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        return {
            '7days': { startDate: sevenDaysAgo.toISOString(), endDate: today.toISOString() },
            '10days': { startDate: tenDaysAgo.toISOString(), endDate: today.toISOString() },
            '30days': { startDate: thirtyDaysAgo.toISOString(), endDate: today.toISOString() },
            '6months': { startDate: sixMonthsAgo.toISOString(), endDate: today.toISOString() },
            '1year': { startDate: oneYearAgo.toISOString(), endDate: today.toISOString() }
        };
    }, []);
    useEffect(() => {
        if (personId) {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                personId: personId,
                date: selectedDate ? selectedDate.toISOString() : undefined
            };
            dispatch(getAllPeopleRecentEvents(request));
        }
    }, [dispatch, paginationModel.page, paginationModel.pageSize, personId]);
    const handleMonthYearChange = (date) => {
        // Reset to first page when date filter changes
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setSelectedDate(date);
        if (personId) {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                personId: personId,
                date: date ? date.toISOString() : undefined
            };
            dispatch(getAllPeopleRecentEvents(request));
        }
    };
    const handleDateRangeSelection = (optionId) => {
        setSelectedRangeOption(optionId);
        if (optionId === 'all') {
            setSelectedDateRange(null);
            setSelectedDate(null);
        }
        else if (optionId === 'custom') {
            // Custom will be handled by date range picker
            return;
        }
        else {
            const range = getDateRanges[optionId];
            if (range) {
                setSelectedDateRange(range);
                setSelectedDate(null);
            }
        }
    };
    const handleCustomDateRangeChange = (range) => {
        if (range) {
            setSelectedDateRange(range);
            setSelectedRangeOption('custom');
            setSelectedDate(null);
        }
    };
    const columns = [
        {
            key: 'eventName',
            label: t('events.eventName') || 'Event Name',
            width: '25%',
            render: (row) => (_jsx(Typography, { variant: 'body2', children: row.eventName }))
        },
        // {
        //   key: 'time',
        //   label: t('events.time') || 'Time',
        //   width: '25%',
        //   render: (row: EventAttended) => <Typography variant='body2'>{row.time}</Typography>
        // },
        {
            key: 'date',
            label: t('events.date') || 'Date',
            width: '25%',
            render: (row) => {
                if (!row.eventDate)
                    return '-';
                const eventDate = new Date(row.eventDate);
                if (isNaN(eventDate.getTime()))
                    return '-';
                return (_jsxs(Typography, { children: [eventDate.getDate(), "/", eventDate.getMonth() + 1, "/", eventDate.getFullYear()] }));
            }
        },
        {
            key: 'interests',
            label: t('settings.interests') || 'Interests',
            width: '25%',
            align: 'center',
            render: (row) => (_jsx(Tooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: row.interests.map((interest, index) => (_jsx(Typography, { variant: 'body2', sx: { py: 0.25 }, children: interest.title }, index))) }), arrow: true, placement: 'bottom', children: _jsx(Chip, { label: String(row.interests.length).padStart(2, '0'), size: 'small', sx: { fontWeight: 400, borderRadius: '500px', cursor: 'pointer' }, variant: 'soft', color: 'default' }) }))
        },
        {
            key: 'eventType',
            label: t('events.eventType') || 'Event Type',
            width: '25%',
            render: (row) => (_jsx(Chip, { color: row.eventType === 'Internal' ? 'success' : 'warning', size: 'medium', label: row.eventType === 'Internal' ? t('events.internal') || 'Internal' : row.eventType, sx: { fontWeight: 400, borderRadius: '8px' }, variant: 'soft' }))
        }
    ];
    return (_jsxs(Box, { sx: {
            backgroundColor: theme.palette.common.whiteTransparent,
            height: '100%'
        }, children: [_jsxs(Box, { sx: { ...between, mb: 1.5 }, children: [_jsx(Typography, { variant: 'h4', color: theme.palette.text.primary, children: t('people.recentEvents') || 'Recent Events' }), _jsx(ButtonDropdown, { label: t('sponsorships.activityTab.monthFilter') || 'Select Date Range', options: getDateRangeOptions(t), variant: 'outlined', size: 'large', color: 'primary', startIcon: _jsx(Calendar2, { variant: 'Outline' }), selectedItem: selectedRangeOption, onSelectionChange: handleDateRangeSelection, onDateRangeChange: handleCustomDateRangeChange, customDateRange: selectedDateRange, enableDateRange: true })] }), _jsx(DataTable, { data: peopleRecentEvent?.items ? peopleRecentEvent.items : [], columns: columns, getRowId: (row) => row.eventName + row.eventDate + row.registeredTime + row.eventType, showSearchFilter: false, pagination: true, page: paginationModel.page, rowsPerPage: paginationModel.pageSize, totalCount: peopleRecentEvent?.totalCount, onPageChange: (page) => setPaginationModel((prev) => ({ ...prev, page })), onRowsPerPageChange: (rowsPerPage) => setPaginationModel((prev) => ({ ...prev, pageSize: rowsPerPage })), rowsPerPageOptions: [5, 10, 25, 50], emptyMessage: t('people.noEventsAttended') || 'No events attended' })] }));
};
export default EventsAttendedTab;
